import React, {useState} from 'react';
import {Label, FlexRow, Modal, Text, FlexColumn, RadioGroupHandler, Frame} from '@forecast-it/design-system';
import {useIntl} from 'react-intl';
import {PERIOD_BUDGET_TYPE} from '../../../constants';
import Util from '../../../forecast-app/shared/util/util';

type RevertRolloverModalProps = {
	closeModal: () => void;
	rollovers: rolloverType[];
	periodBudgetType: string;
	currency: string;
	unlockPeriodMutations: (rolloverIdsToRevert: string[] | undefined) => void;
	isBulkUnlock: boolean;
};

type periodFromToType = {
	id: string;
	name: string;
};

type rolloverType = {
	id: string;
	periodFrom: periodFromToType | null;
	periodTo: periodFromToType | null;
	amount: number | null;
};

const RevertRolloverModal = ({
	rollovers,
	periodBudgetType,
	currency,
	closeModal,
	unlockPeriodMutations,
	isBulkUnlock,
}: RevertRolloverModalProps): JSX.Element => {
	const intl = useIntl();
	const {formatMessage, formatNumber} = intl;
	const rolloverIdsToRevert = rollovers.map(rollover => rollover.id);
	const subtractedRollovers = rollovers.filter(rollover => rollover.amount && rollover.amount < 0);
	const addedRollovers = rollovers.filter(rollover => rollover.amount && rollover.amount >= 0);
	const hasSubtractedRollovers = subtractedRollovers.length > 0;
	const hasAddedRollovers = addedRollovers.length > 0;
	const isFixedPrice = periodBudgetType === PERIOD_BUDGET_TYPE.FIXED_PRICE;
	const addedRolloverPeriods: periodFromToType[] = [];
	const subtractedRolloverPeriods: periodFromToType[] = [];
	if (isBulkUnlock) {
		addedRollovers.forEach(rollover => {
			const periodFrom = rollover.periodFrom;
			if (periodFrom && !addedRolloverPeriods.some(period => period.id === periodFrom.id)) {
				addedRolloverPeriods.push(periodFrom);
			}
		});
		subtractedRollovers.forEach(rollover => {
			const periodFrom = rollover.periodFrom;
			if (periodFrom && !subtractedRolloverPeriods.some(period => period.id === periodFrom.id)) {
				subtractedRolloverPeriods.push(periodFrom);
			}
		});
	}

	const [revertRollovers, setRevertRollovers] = useState<string>('false');

	const saveChanges = () => {
		closeModal();
		unlockPeriodMutations(revertRollovers === 'true' ? rolloverIdsToRevert : undefined);
	};

	return (
		<Modal size={Modal.SIZE.MEDIUM} onCloseModal={closeModal}>
			<Modal.Title>
				{formatMessage({
					id: hasSubtractedRollovers
						? hasAddedRollovers
							? 'retainer.revert_rollover_modal.add_and_subtract_headline'
							: 'retainer.revert_rollover_modal.subtract_headline'
						: 'retainer.revert_rollover_modal.add_headline',
				})}
			</Modal.Title>
			<Modal.Content>
				<FlexColumn gap={'l'}>
					{hasAddedRollovers && (
						<FlexColumn gap={'s'}>
							<Text>
								{formatMessage({
									id: isFixedPrice
										? isBulkUnlock
											? 'retainer.revert_rollover_modal.add_info_price_bulk'
											: 'retainer.revert_rollover_modal.add_info_price'
										: isBulkUnlock
										? 'retainer.revert_rollover_modal.add_info_hours_bulk'
										: 'retainer.revert_rollover_modal.add_info_hours',
								})}
							</Text>
							<Frame>
								<FlexColumn gap={'xxs'}>
									{isBulkUnlock
										? addedRolloverPeriods.map(period => <Text>{period.name}</Text>)
										: addedRollovers.map(rollover => {
												return rollover.amount && rollover.periodTo ? (
													<Text>
														{`${rollover.periodTo.name} (${
															isFixedPrice
																? Util.getFormattedCurrencyValue(
																		Util.GetCurrencySymbol(currency),
																		formatNumber(rollover.amount, {
																			format: 'always_two_decimal',
																		})
																  )
																: Util.convertMinutesToFullHour(rollover.amount * 60, intl)
														})`}
													</Text>
												) : null;
										  })}
								</FlexColumn>
							</Frame>
						</FlexColumn>
					)}
					{hasSubtractedRollovers && (
						<FlexColumn gap={'s'}>
							<Text>
								{formatMessage({
									id: isFixedPrice
										? isBulkUnlock
											? 'retainer.revert_rollover_modal.subtract_info_price_bulk'
											: 'retainer.revert_rollover_modal.subtract_info_price'
										: isBulkUnlock
										? 'retainer.revert_rollover_modal.subtract_info_hours_bulk'
										: 'retainer.revert_rollover_modal.subtract_info_hours',
								})}
							</Text>
							<Frame>
								<FlexColumn gap={'xxs'}>
									{isBulkUnlock
										? subtractedRolloverPeriods.map(period => <Text>{period.name}</Text>)
										: subtractedRollovers.map(rollover => {
												return rollover.amount && rollover.periodTo ? (
													<Text>
														{`${rollover.periodTo.name} (${
															isFixedPrice
																? Util.getFormattedCurrencyValue(
																		Util.GetCurrencySymbol(currency),
																		formatNumber(Math.abs(rollover.amount), {
																			format: 'always_two_decimal',
																		})
																  )
																: Util.convertMinutesToFullHour(
																		Math.abs(rollover.amount) * 60,
																		intl
																  )
														})`}
													</Text>
												) : null;
										  })}
								</FlexColumn>
							</Frame>
						</FlexColumn>
					)}
					<FlexColumn gap={'xxs'}>
						<Label>
							{formatMessage({
								id: hasSubtractedRollovers
									? hasAddedRollovers
										? isFixedPrice
											? isBulkUnlock
												? 'retainer.revert_rollover_modal.revert_add_and_subtract_headline_price_bulk'
												: 'retainer.revert_rollover_modal.revert_add_and_subtract_headline_price'
											: isBulkUnlock
											? 'retainer.revert_rollover_modal.revert_add_and_subtract_headline_hours_bulk'
											: 'retainer.revert_rollover_modal.revert_add_and_subtract_headline_hours'
										: isFixedPrice
										? isBulkUnlock
											? 'retainer.revert_rollover_modal.revert_subtract_headline_price_bulk'
											: 'retainer.revert_rollover_modal.revert_subtract_headline_price'
										: isBulkUnlock
										? 'retainer.revert_rollover_modal.revert_subtract_headline_hours_bulk'
										: 'retainer.revert_rollover_modal.revert_subtract_headline_hours'
									: isFixedPrice
									? isBulkUnlock
										? 'retainer.revert_rollover_modal.revert_add_headline_price_bulk'
										: 'retainer.revert_rollover_modal.revert_add_headline_price'
									: isBulkUnlock
									? 'retainer.revert_rollover_modal.revert_add_headline_hours_bulk'
									: 'retainer.revert_rollover_modal.revert_add_headline_hours',
							})}
						</Label>
						<Text color={'medium'}>
							{formatMessage({
								id: isBulkUnlock
									? 'retainer.revert_rollover_modal.revert_info_bulk'
									: hasSubtractedRollovers
									? hasAddedRollovers
										? isFixedPrice
											? 'retainer.revert_rollover_modal.revert_add_and_subtract_info_price'
											: 'retainer.revert_rollover_modal.revert_add_and_subtract_info_hours'
										: isFixedPrice
										? 'retainer.revert_rollover_modal.revert_subtract_info_price'
										: 'retainer.revert_rollover_modal.revert_subtract_info_hours'
									: isFixedPrice
									? 'retainer.revert_rollover_modal.revert_add_info_price'
									: 'retainer.revert_rollover_modal.revert_add_info_hours',
							})}
						</Text>
					</FlexColumn>
					<RadioGroupHandler onValueChange={setRevertRollovers} value={revertRollovers}>
						<FlexRow gap={'s'}>
							<RadioGroupHandler.Item value={'true'} />
							<Text>{formatMessage({id: 'retainer.revert_rollover_modal.revert_radio'})}</Text>
						</FlexRow>
						<FlexRow gap={'s'}>
							<RadioGroupHandler.Item value={'false'} />
							<Text>{formatMessage({id: 'retainer.revert_rollover_modal.dont_revert_radio'})}</Text>
						</FlexRow>
					</RadioGroupHandler>
				</FlexColumn>
			</Modal.Content>
			<Modal.Footer>
				<Modal.PrimaryFooterButton onPress={saveChanges} disabled={revertRollovers === null}>
					{formatMessage({id: 'common.confirm'})}
				</Modal.PrimaryFooterButton>
				<Modal.SecondaryFooterButton onPress={closeModal}>
					{formatMessage({id: 'common.cancel'})}
				</Modal.SecondaryFooterButton>
			</Modal.Footer>
		</Modal>
	);
};

export default RevertRolloverModal;
